import React from "react";
import { RefreshCw, Trash2, MapPin, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import TimeInput from "@/components/TimeInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { ReceiptsList } from "@/components/receipts/ReceiptsList";
import { CreatedByLine } from "@/components/shared/CreatedByLine";
import { toast } from "sonner";
import api from "@/lib/api";

export const BookingDetailsDialog = ({
  open, onOpenChange,
  selectedBooking,
  selectedStudio,
  detailsTab, setDetailsTab,
  formatTime12,
  PAYMENT_STATUS_COLORS,
  resources,
  // edit
  editForm, setEditForm,
  editTotal,
  toggleEditResource, updateEditResourceQuantity,
  saveBookingEdit,
  // payment
  payForm, setPayForm,
  recordPayment,
  // cancel flow
  cancelConfirmStep, setCancelConfirmStep,
  cancelFuture, setCancelFuture,
  initCancelBooking,
  executeCancelBooking,
  // Apr 2026 — refetch trigger after room change.
  onBookingMutated,
}) => {
  // Apr 2026 — Room change with scope (calendar parity with class instructor/room).
  // Local UI state for the room-change sub-section. Lives inside the Edit tab.
  const [roomChange, setRoomChange] = React.useState({
    newRoomId: "",
    scope: "this_only",      // "this_only" | "future_only"
    saving: false,
    conflictDetail: null,    // 409 payload with per-occurrence conflicts
  });

  React.useEffect(() => {
    // Reset whenever the dialog opens for a new booking.
    setRoomChange({
      newRoomId: selectedBooking?.room_id || "",
      scope: "this_only",
      saving: false,
      conflictDetail: null,
    });
  }, [selectedBooking?.booking_id, selectedBooking?.room_id]);

  const isRecurring = !!selectedBooking?.recurring_group_id;
  const currentRoom = (selectedStudio?.rooms || []).find((r) => r.room_id === selectedBooking?.room_id);

  const submitRoomChange = async () => {
    const targetRoomId = roomChange.newRoomId;
    if (!selectedBooking?.booking_id) return;
    if (!targetRoomId || targetRoomId === selectedBooking.room_id) {
      toast.error("Pick a different room.");
      return;
    }
    setRoomChange((c) => ({ ...c, saving: true, conflictDetail: null }));
    try {
      const res = await api.put(`/bookings/${selectedBooking.booking_id}/change-room`, {
        room_id: targetRoomId,
        change_scope: isRecurring ? roomChange.scope : "this_only",
      });
      const n = res.data?.updated_count ?? 0;
      toast.success(
        n <= 1
          ? "Room changed successfully"
          : `Room changed for ${n} bookings`
      );
      setRoomChange((c) => ({ ...c, saving: false }));
      onBookingMutated?.();
      onOpenChange?.(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "room_change_conflicts") {
        setRoomChange((c) => ({ ...c, saving: false, conflictDetail: detail }));
        toast.error(detail.message || "Conflicts found — none of the bookings were changed.");
        return;
      }
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to change room");
      toast.error(msg);
      setRoomChange((c) => ({ ...c, saving: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{selectedBooking?.renter_name || "Booking"}</DialogTitle>
          <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-hide">
            {["info", "edit", "pay", "history"].map(tab => (
              <button key={tab} onClick={() => setDetailsTab(tab)} className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${detailsTab === tab ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                {tab === "info" && "Details"}
                {tab === "edit" && "Edit"}
                {tab === "pay" && "Record Payment"}
                {tab === "history" && "History"}
              </button>
            ))}
          </div>
        </DialogHeader>

        {selectedBooking && (
          <div className="space-y-4">
            {detailsTab === "info" && (
              <div className="space-y-3">
                {selectedBooking.recurring_group_id ? (
                  <>
                    {/* THIS OCCURRENCE block */}
                    <div className="p-3 bg-violet-50 border border-violet-200 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-violet-800 uppercase tracking-wide">This Occurrence</span>
                        {selectedBooking.recurrence_instance_number && selectedBooking.instances_count && (
                          <span className="text-[10px] text-violet-600 font-medium">
                            #{selectedBooking.recurrence_instance_number} of {selectedBooking.instances_count}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500 text-xs">Customer</span><p className="font-medium">{selectedBooking.renter_name}</p></div>
                        <div><span className="text-slate-500 text-xs">Phone</span><p className="font-medium">{selectedBooking.renter_phone}</p></div>
                        <div><span className="text-slate-500 text-xs">Time</span><p className="font-medium">{formatTime12(selectedBooking.start_time)} - {formatTime12(selectedBooking.end_time)}</p></div>
                        <div><span className="text-slate-500 text-xs">Studio</span><p className="font-medium">{selectedStudio?.name}</p></div>
                      </div>
                    </div>

                    {/* SERIES PAYMENT block */}
                    <div className="p-3 bg-slate-50 rounded-xl space-y-2 border border-slate-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Series Payment</span>
                        <span className="flex items-center gap-1 text-[10px] text-blue-600">
                          <RefreshCw className="w-3 h-3" /> {selectedBooking.instances_count || "?"} occurrences
                        </span>
                      </div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Series Rent</span><span className="font-medium">₹{selectedBooking.agreed_rent}</span></div>
                      {(selectedBooking.discount || 0) > 0 && (
                        <div className="flex justify-between text-sm"><span className="text-slate-500">Discount</span><span className="font-medium text-green-600">-₹{selectedBooking.discount_type === "percent" ? Math.round(selectedBooking.agreed_rent * selectedBooking.discount / 100) : selectedBooking.discount} {selectedBooking.discount_type === "percent" ? `(${selectedBooking.discount}%)` : ""}</span></div>
                      )}
                      <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Total Due</span><span>₹{selectedBooking.total_amount || selectedBooking.agreed_rent}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Paid</span><span className="font-medium text-green-700">₹{selectedBooking.amount_paid || 0}</span></div>
                      <div className="flex justify-between text-sm font-semibold"><span>Balance</span><span className="text-red-600">₹{selectedBooking.balance_amount ?? (selectedBooking.total_amount || selectedBooking.agreed_rent) - (selectedBooking.amount_paid || 0)}</span></div>
                      <div className="flex justify-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${PAYMENT_STATUS_COLORS[selectedBooking.rent_payment_status || "pending"]?.badge}`}>
                          {(selectedBooking.rent_payment_status || "pending").toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500 text-xs">Customer</span><p className="font-medium">{selectedBooking.renter_name}</p></div>
                      <div><span className="text-slate-500 text-xs">Phone</span><p className="font-medium">{selectedBooking.renter_phone}</p></div>
                      <div><span className="text-slate-500 text-xs">Time</span><p className="font-medium">{formatTime12(selectedBooking.start_time)} - {formatTime12(selectedBooking.end_time)}</p></div>
                      <div><span className="text-slate-500 text-xs">Studio</span><p className="font-medium">{selectedStudio?.name}</p></div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Agreed Rent</span><span className="font-medium">₹{selectedBooking.agreed_rent}</span></div>
                      {(selectedBooking.discount || 0) > 0 && (
                        <div className="flex justify-between text-sm"><span className="text-slate-500">Discount</span><span className="font-medium text-green-600">-₹{selectedBooking.discount_type === "percent" ? Math.round(selectedBooking.agreed_rent * selectedBooking.discount / 100) : selectedBooking.discount} {selectedBooking.discount_type === "percent" ? `(${selectedBooking.discount}%)` : ""}</span></div>
                      )}
                      <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Total Due</span><span>₹{selectedBooking.total_amount || selectedBooking.agreed_rent}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Paid</span><span className="font-medium text-green-700">₹{selectedBooking.amount_paid || 0}</span></div>
                      <div className="flex justify-between text-sm font-semibold"><span>Balance</span><span className="text-red-600">₹{selectedBooking.balance_amount ?? (selectedBooking.total_amount || selectedBooking.agreed_rent) - (selectedBooking.amount_paid || 0)}</span></div>
                      <div className="flex justify-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${PAYMENT_STATUS_COLORS[selectedBooking.rent_payment_status || "pending"]?.badge}`}>
                          {(selectedBooking.rent_payment_status || "pending").toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                {selectedBooking.notes && <div className="text-sm"><span className="text-slate-500 text-xs">Notes</span><p>{selectedBooking.notes}</p></div>}
                <CreatedByLine
                  name={selectedBooking.created_by_name}
                  status={selectedBooking.created_by_status}
                  date={selectedBooking.created_at}
                  variant="detail"
                  testid="booking-details-created-by"
                  className="pt-2 mt-2 border-t border-slate-100"
                />

                {(selectedBooking.resources_required || []).length > 0 && (
                  <div className="text-sm">
                    <span className="text-slate-500 text-xs">Resources</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedBooking.resources_required.map((r, i) => {
                        const res = resources.find(rs => rs.resource_id === r.resource_id);
                        return (
                          <span key={`${r.resource_id}-${i}`} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                            {res?.name || r.resource_id} x{r.quantity}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {cancelConfirmStep === null && (
                  <Button onClick={initCancelBooking} variant="outline" className="w-full rounded-xl text-red-600 border-red-200 hover:bg-red-50 text-sm">
                    <Trash2 className="w-4 h-4 mr-1" /> Cancel Booking
                  </Button>
                )}
                {cancelConfirmStep === "recurring_choice" && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                    <p className="text-sm font-medium text-blue-800">This is a recurring booking. What would you like to cancel?</p>
                    <div className="flex flex-col gap-2">
                      <Button onClick={() => { setCancelFuture(false); const hasPaid = (selectedBooking.amount_paid || 0) > 0; setCancelConfirmStep(hasPaid ? "refund" : "confirm"); }} size="sm" variant="outline" className="rounded-lg text-xs justify-start">Just this event</Button>
                      <Button onClick={() => { setCancelFuture(true); const hasPaid = (selectedBooking.amount_paid || 0) > 0; setCancelConfirmStep(hasPaid ? "refund" : "confirm"); }} size="sm" variant="outline" className="rounded-lg text-xs justify-start text-red-600 border-red-200">This & all future events</Button>
                      <Button onClick={() => setCancelConfirmStep(null)} size="sm" variant="ghost" className="rounded-lg text-xs text-slate-500">Back</Button>
                    </div>
                  </div>
                )}
                {cancelConfirmStep === "refund" && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                    <p className="text-sm font-medium text-amber-800">
                      {cancelFuture ? "Some bookings in this series may have payments." : `This booking has ₹${selectedBooking.amount_paid || 0} in payments.`} Was it refunded?
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => { setCancelConfirmStep("confirm_refunded"); }} size="sm" className="flex-1 rounded-lg bg-green-600 hover:bg-green-700 text-xs">Yes, Refunded</Button>
                      <Button onClick={() => { setCancelConfirmStep("confirm_not_refunded"); }} size="sm" variant="outline" className="flex-1 rounded-lg text-xs">No Refund</Button>
                      <Button onClick={() => setCancelConfirmStep(selectedBooking.recurring_group_id ? "recurring_choice" : null)} size="sm" variant="ghost" className="rounded-lg text-xs text-slate-500">Back</Button>
                    </div>
                  </div>
                )}
                {(cancelConfirmStep === "confirm" || cancelConfirmStep === "confirm_refunded" || cancelConfirmStep === "confirm_not_refunded") && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-3">
                    <p className="text-sm font-medium text-red-800">
                      {cancelFuture ? "Cancel this and all future events in this series?" : "Are you sure you want to cancel this booking?"}
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => executeCancelBooking(cancelConfirmStep === "confirm_refunded" ? true : cancelConfirmStep === "confirm_not_refunded" ? false : null)} size="sm" className="flex-1 rounded-lg bg-red-600 hover:bg-red-700 text-xs">
                        {cancelFuture ? "Yes, Cancel All" : "Yes, Cancel"}
                      </Button>
                      <Button onClick={() => { setCancelConfirmStep(null); setCancelFuture(false); }} size="sm" variant="outline" className="flex-1 rounded-lg text-xs">No, Keep It</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {detailsTab === "edit" && (
              <div className="space-y-3">
                {selectedBooking?.created_by_name && (
                  <CreatedByLine
                    name={selectedBooking.created_by_name}
                    status={selectedBooking.created_by_status}
                    date={selectedBooking.created_at}
                    variant="detail"
                    testid="booking-edit-created-by"
                    className="pb-2 border-b border-slate-100"
                  />
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Booked By</Label><Input value={editForm.renter_name} onChange={(e) => setEditForm({...editForm, renter_name: e.target.value})} className="h-10 rounded-xl text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={editForm.renter_phone} onChange={(e) => setEditForm({...editForm, renter_phone: e.target.value})} className="h-10 rounded-xl text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Start</Label><TimeInput value={editForm.start_time} onChange={(e) => setEditForm({...editForm, start_time: e.target.value})} /></div>
                  <div className="space-y-1"><Label className="text-xs">End</Label><TimeInput value={editForm.end_time} onChange={(e) => setEditForm({...editForm, end_time: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Rent (₹)</Label><Input type="number" value={editForm.agreed_rent} onChange={(e) => setEditForm({...editForm, agreed_rent: e.target.value})} className="h-10 rounded-xl text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Discount</Label><Input type="number" value={editForm.discount} onChange={(e) => setEditForm({...editForm, discount: e.target.value})} className="h-10 rounded-xl text-sm" /></div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <select value={editForm.discount_type} onChange={(e) => setEditForm({...editForm, discount_type: e.target.value})} className="w-full h-10 rounded-xl border px-2 bg-white text-sm">
                      <option value="flat">₹ Flat</option>
                      <option value="percent">%</option>
                    </select>
                  </div>
                </div>
                <div className="px-3 py-2 bg-slate-100 rounded-xl text-sm font-medium">New Total: ₹{editTotal}</div>
                <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} className="rounded-xl text-sm" rows={2} /></div>

                <div className="space-y-1">
                  <Label className="text-xs">Calendar Color (optional)</Label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      data-testid="booking-edit-color-input"
                      type="color"
                      value={editForm.color || "#7C3AED"}
                      onChange={(e) => setEditForm({...editForm, color: e.target.value})}
                      className="h-9 w-12 cursor-pointer rounded-md border border-slate-300"
                    />
                    <span className="text-xs text-slate-500">
                      {editForm.color
                        ? <>Selected: <code className="text-slate-700">{editForm.color}</code></>
                        : "Uses payment-status color by default"}
                    </span>
                    {editForm.color && (
                      <button
                        type="button"
                        onClick={() => setEditForm({...editForm, color: ""})}
                        className="text-xs text-violet-600 hover:underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {selectedBooking?.recurring_group_id && (
                    <div className="mt-2 flex gap-2" data-testid="booking-edit-color-scope">
                      {[
                        { v: "this_only", label: "This Booking Only" },
                        { v: "future_only", label: "This & Future" },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          data-testid={`color-scope-${opt.v}`}
                          onClick={() => setEditForm({...editForm, color_change_scope: opt.v})}
                          className={`text-xs px-3 py-1.5 rounded-full border transition ${
                            (editForm.color_change_scope || "this_only") === opt.v
                              ? "border-violet-500 bg-violet-50 text-violet-700"
                              : "border-slate-200 bg-white text-slate-600 hover:border-violet-200"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {resources.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Resources Required</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                      {resources.map(resource => {
                        const selected = (editForm.resources_required || []).find(r => r.resource_id === resource.resource_id);
                        return (
                          <div key={resource.resource_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                            <input
                              type="checkbox"
                              checked={!!selected}
                              onChange={() => toggleEditResource(resource.resource_id)}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm flex-1 truncate">{resource.name} <span className="text-xs text-slate-400">(avail: {resource.quantity})</span></span>
                            {selected && (
                              <Input
                                type="number"
                                min="1"
                                max={resource.quantity}
                                value={selected.quantity}
                                onChange={(e) => updateEditResourceQuantity(resource.resource_id, e.target.value)}
                                className="w-16 h-8 text-sm rounded-lg"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <Button onClick={saveBookingEdit} className="w-full rounded-xl h-11 bg-primary">Save Changes</Button>

                {/* Apr 2026 — Change Room (parity with class instructor/room change).
                    Operational-only: NO financial fields are mutated. */}
                <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-violet-600" />
                    <span className="text-sm font-semibold text-slate-800">Change Room</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Current Room</Label>
                      <div className="h-10 rounded-xl border bg-white px-3 flex items-center text-sm text-slate-600">
                        {currentRoom?.name || selectedBooking.room_id}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">New Room</Label>
                      <select
                        data-testid="rental-change-room-select"
                        value={roomChange.newRoomId}
                        onChange={(e) => setRoomChange((c) => ({ ...c, newRoomId: e.target.value, conflictDetail: null }))}
                        className="w-full h-10 rounded-xl border px-2 bg-white text-sm"
                      >
                        {(selectedStudio?.rooms || []).map((r) => (
                          <option key={r.room_id} value={r.room_id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {isRecurring && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Apply Change To</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[
                          { value: "this_only", title: "This Booking Only", subtitle: "Single occurrence" },
                          { value: "future_only", title: "This & Future", subtitle: "Past bookings unchanged" },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            data-testid={`rental-room-scope-${opt.value}`}
                            onClick={() => setRoomChange((c) => ({ ...c, scope: opt.value, conflictDetail: null }))}
                            className={`p-2.5 rounded-lg border text-left transition ${
                              roomChange.scope === opt.value
                                ? "border-violet-500 bg-violet-50"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <div className="text-sm font-semibold">{opt.title}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{opt.subtitle}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {roomChange.conflictDetail && (
                    <div className="p-2.5 rounded-lg bg-red-50 border border-red-200 text-red-800">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <AlertTriangle className="w-4 h-4" /> Cannot move — destination room is occupied
                      </div>
                      <p className="text-xs mt-1">{roomChange.conflictDetail.message}</p>
                      <ul className="mt-2 text-xs space-y-1 max-h-24 overflow-y-auto">
                        {(roomChange.conflictDetail.blocking || []).slice(0, 8).map((b) => (
                          <li key={b.booking_id} className="flex justify-between">
                            <span>{b.booking_date}</span>
                            <span>{b.start_time}–{b.end_time}</span>
                          </li>
                        ))}
                        {(roomChange.conflictDetail.blocking || []).length > 8 && (
                          <li className="text-slate-500">…and more</li>
                        )}
                      </ul>
                    </div>
                  )}

                  <Button
                    onClick={submitRoomChange}
                    disabled={roomChange.saving || !roomChange.newRoomId || roomChange.newRoomId === selectedBooking.room_id}
                    data-testid="rental-confirm-room-change"
                    className="w-full rounded-xl h-10 bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
                  >
                    {roomChange.saving ? "Changing…" : "Change Room"}
                  </Button>
                </div>
              </div>
            )}

            {detailsTab === "pay" && (
              <div className="space-y-3">
                <div className="p-3 bg-slate-50 rounded-xl flex justify-between text-sm">
                  <span className="text-slate-500">Outstanding Balance</span>
                  <span className="font-bold text-red-600">₹{selectedBooking.balance_amount ?? (selectedBooking.total_amount || selectedBooking.agreed_rent) - (selectedBooking.amount_paid || 0)}</span>
                </div>
                {(selectedBooking.balance_amount || 0) > 0 || selectedBooking.rent_payment_status !== "paid" ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Amount *</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({...payForm, amount: e.target.value})} placeholder="₹" className="h-10 rounded-xl text-sm" /></div>
                      <div className="space-y-1">
                        <Label className="text-xs">Method</Label>
                        <select value={payForm.payment_method} onChange={(e) => setPayForm({...payForm, payment_method: e.target.value})} className="w-full h-10 rounded-xl border px-2 bg-white text-sm">
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({...payForm, payment_date: e.target.value})} className="h-10 rounded-xl text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">Reference #</Label><Input value={payForm.reference_number} onChange={(e) => setPayForm({...payForm, reference_number: e.target.value})} placeholder="Optional" className="h-10 rounded-xl text-sm" /></div>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Notes</Label><Input value={payForm.notes} onChange={(e) => setPayForm({...payForm, notes: e.target.value})} placeholder="Optional" className="h-10 rounded-xl text-sm" /></div>
                    <Button onClick={recordPayment} className="w-full rounded-xl h-11 bg-green-600 hover:bg-green-700">Record Payment</Button>
                  </>
                ) : (
                  <p className="text-center text-green-600 font-medium py-4">Fully Paid</p>
                )}
              </div>
            )}

            {detailsTab === "history" && (
              <div className="space-y-4">
                {/* Receipts */}
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Receipts</p>
                  <ReceiptsList
                    {...(selectedBooking.recurring_group_id
                      ? { recurringGroupId: selectedBooking.recurring_group_id }
                      : { bookingId: selectedBooking.booking_id })}
                    refreshKey={selectedBooking.amount_paid || 0}
                  />
                </div>

                {/* Payment transactions */}
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Transactions</p>
                  {(selectedBooking.payment_history || []).length === 0 ? (
                    <p className="text-center text-slate-500 py-4 text-sm">No payments recorded</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedBooking.payment_history.map((p, i) => (
                        <div key={`${p.date || ""}-${p.amount}-${p.method || ""}-${i}`} className="p-3 bg-slate-50 rounded-xl text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="font-semibold text-green-700">₹{p.amount}</span>
                            <span className="text-xs text-slate-500">{p.date ? format(new Date(p.date), 'dd MMM yyyy') : "N/A"}</span>
                          </div>
                          <div className="flex gap-2 text-xs text-slate-500">
                            <span className="capitalize">{p.method?.replace("_", " ")}</span>
                            {p.reference_number && <span>Ref: {p.reference_number}</span>}
                          </div>
                          {p.notes && <p className="text-xs text-slate-400">{p.notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailsDialog;
